import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, Slug, slugify, type TShowSeries } from '@schemas'
import {
  reportActorKey,
  useCurrentUser,
  useShowSeries,
  useShowSeriesDetail,
  useSeriesEditions,
  useCreateShowSeries,
  useUpdateShowSeries,
} from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

export function SeriesField({
  organizationId,
  value,
  retainedSeries,
  onChange,
  disabled,
  onValidityChange,
  showEditions = false,
}: {
  organizationId?: string
  value: string | null | undefined
  retainedSeries?: TShowSeries | null
  onChange: (value: string | null | undefined) => void
  disabled?: boolean
  onValidityChange?: (valid: boolean) => void
  showEditions?: boolean
}) {
  const id = useId()
  const user = useCurrentUser()
  const query = useShowSeries(organizationId)
  const detail = useShowSeriesDetail(value)
  const create = useCreateShowSeries()
  const update = useUpdateShowSeries()
  const [dialog, setDialog] = useState<'create' | 'rename' | null>(null)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [renameRevision, setRenameRevision] = useState<number | null>(null)
  const [renameStale, setRenameStale] = useState(false)
  const [savedRename, setSavedRename] = useState<TShowSeries | null>(null)
  const [reloadingRename, setReloadingRename] = useState(false)
  const renameRequest = useRef(0)
  const [created, setCreated] = useState<TShowSeries | null>(null)
  const [deniedSeriesId, setDeniedSeriesId] = useState<string | null>(null)
  const scope = JSON.stringify(reportActorKey(user.data))
  const previousScope = useRef(scope)
  const previousForbidden = useRef(false)
  const forbidden = [query.error, detail.error].some(
    error => error instanceof ApiError && [401, 403, 404].includes(error.status)
  )
  useEffect(() => {
    if (previousScope.current !== scope || (forbidden && !previousForbidden.current)) {
      const scopeChanged = previousScope.current !== scope
      previousScope.current = scope
      setDeniedSeriesId(scopeChanged ? null : (value ?? null))
      onChange(undefined)
      setCreated(null)
      renameRequest.current += 1
      setRenameStale(false)
      setSavedRename(null)
      setReloadingRename(false)
      setRenameRevision(null)
      setDialog(null)
      setName('')
      setSlug('')
      setError(null)
    }
    previousForbidden.current = forbidden
  }, [scope, forbidden, onChange, value])
  useEffect(() => {
    // A dialog belongs to the edition selection that opened it.
    renameRequest.current += 1
    setDialog(null)
    setName('')
    setSavedRename(null)
  }, [organizationId, value])
  const sameActorScope = previousScope.current === scope
  const available =
    forbidden || !sameActorScope
      ? []
      : (query.data?.pages
          .flatMap(page => page.series)
          .filter(series => series.id !== deniedSeriesId) ?? [])
  const selected =
    forbidden || !sameActorScope || value === deniedSeriesId
      ? null
      : (detail.data?.series ??
        available.find(series => series.id === value) ??
        (created?.id === value ? created : retainedSeries?.id === value ? retainedSeries : null))
  const valid =
    !value || Boolean(selected && selected.organizationId === organizationId && !forbidden)
  useEffect(() => {
    onValidityChange?.(valid)
  }, [valid, onValidityChange])
  const options = [...available]
  if (selected && !options.some(series => series.id === selected.id)) options.push(selected)
  const open = (mode: 'create' | 'rename') => {
    renameRequest.current += 1
    setRenameRevision(mode === 'rename' ? (selected?.metadataRevision ?? null) : null)
    setRenameStale(false)
    setSavedRename(null)
    setReloadingRename(false)
    setDialog(mode)
    setError(null)
    setName(mode === 'rename' ? (selected?.name ?? '') : '')
    setSlug('')
    setSlugEdited(false)
  }
  const reloadRename = async () => {
    const requestId = ++renameRequest.current
    setReloadingRename(true)
    setSavedRename(null)
    try {
      const refreshed = await detail.refetch({ throwOnError: true })
      if (requestId !== renameRequest.current) return
      if (!refreshed.data?.series) throw new Error('No saved series returned')
      setSavedRename(refreshed.data.series)
    } catch {
      if (requestId === renameRequest.current)
        setError('Unable to reload the saved series. Retry loading it before saving.')
    } finally {
      if (requestId === renameRequest.current) setReloadingRename(false)
    }
  }
  const save = async () => {
    if (dialog === 'rename' && (renameStale || !selected || renameRevision === null)) return
    const requestId = renameRequest.current
    const trimmed = name.trim()
    if (!trimmed || trimmed.length > 200) {
      setError('Enter a series name of 1–200 characters.')
      return
    }
    if (dialog === 'create' && !Slug.safeParse(slug).success) {
      setError('Enter a valid series slug using lowercase letters, numbers and hyphens.')
      return
    }
    try {
      const result =
        dialog === 'create'
          ? await create.mutateAsync({ organizationId: organizationId!, name: trimmed, slug })
          : await update.mutateAsync({
              seriesId: selected!.id,
              payload: { name: trimmed, expectedMetadataRevision: renameRevision! },
            })
      if (requestId !== renameRequest.current) return
      setCreated(result.series)
      onChange(result.series.id)
      setDialog(null)
    } catch (failure) {
      if (requestId !== renameRequest.current) return
      setError(
        failure instanceof ApiError
          ? String(failure.payload?.message ?? 'Unable to save series.')
          : 'Unable to save series. Try again.'
      )
      if (failure instanceof ApiError && failure.payload?.code === 'SERIES_METADATA_STALE') {
        setRenameStale(true)
        await reloadRename()
      }
    }
  }
  return (
    <div className="space-y-3">
      <Label htmlFor={id}>Show series</Label>
      <select
        id={id}
        aria-describedby={`${id}-help`}
        aria-invalid={!valid}
        className="h-10 w-full rounded-md border bg-background px-3 text-sm"
        value={value ?? ''}
        disabled={disabled || !organizationId || forbidden}
        onChange={event => onChange(event.target.value || null)}
      >
        <option value="">None</option>
        {value && !options.some(series => series.id === value) && (
          <option value={value}>Selected series unavailable</option>
        )}
        {options.map(series => (
          <option key={series.id} value={series.id}>
            {series.name}
          </option>
        ))}
      </select>
      <div id={`${id}-help`} className="space-y-1 text-sm text-muted-foreground">
        <p>
          Optional recurring series. One edition per UTC start-date year. Each show is compared with
          the edition that starts most recently before it.
        </p>
        {query.isLoading && <p role="status">Loading series…</p>}
        {query.isError && (
          <p role="alert">
            Unable to load series.{' '}
            <Button type="button" variant="link" onClick={() => void query.refetch()}>
              Retry series
            </Button>
          </p>
        )}
        {query.isSuccess && !available.length && (
          <p>
            No available series for this organization. An administrator can create a series and
            establish its first membership.
          </p>
        )}
        {!valid && <p role="alert">Choose a series for this organization or None before saving.</p>}
      </div>
      {query.hasNextPage && (
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          More series
        </Button>
      )}
      {user.data?.isAdmin && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={disabled || !organizationId}
            onClick={() => open('create')}
          >
            Create series
          </Button>
          {selected && (
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => open('rename')}
            >
              Rename series
            </Button>
          )}
        </div>
      )}
      {showEditions && value && valid && !forbidden && <SeriesEditions seriesId={value} />}
      {sameActorScope && !forbidden && (
        <Dialog
          open={dialog !== null}
          onOpenChange={open => {
            if (!open) {
              renameRequest.current += 1
              setDialog(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialog === 'rename' ? 'Rename series' : 'Create series'}</DialogTitle>
              <DialogDescription>
                Series names are shared with everyone who can read an edition. This action saves
                series metadata immediately; save show settings separately to apply membership.
              </DialogDescription>
            </DialogHeader>
            <Label htmlFor={`${id}-name`}>Series name</Label>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={200}
              onChange={event => {
                setName(event.target.value)
                if (!slugEdited) setSlug(slugify(event.target.value))
              }}
            />
            {dialog === 'create' && (
              <>
                <Label htmlFor={`${id}-slug`}>Series slug</Label>
                <Input
                  id={`${id}-slug`}
                  value={slug}
                  onChange={event => {
                    setSlugEdited(true)
                    setSlug(event.target.value)
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  The organization and slug cannot change after creation.
                </p>
              </>
            )}
            {error && <p role="alert">{error}</p>}
            {renameStale && sameActorScope && !forbidden && (
              <div className="space-y-2" role="status">
                {reloadingRename ? (
                  <p>Loading the saved series name…</p>
                ) : savedRename ? (
                  <>
                    <p>
                      Saved series name: {savedRename.name}. Your draft is unchanged. Review this
                      name before saving again.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setRenameRevision(savedRename.metadataRevision)
                        setRenameStale(false)
                        setSavedRename(null)
                        setError(null)
                      }}
                    >
                      I have reviewed the saved name
                    </Button>
                  </>
                ) : (
                  <Button type="button" variant="outline" onClick={() => void reloadRename()}>
                    Reload saved series
                  </Button>
                )}
              </div>
            )}
            <Button
              type="button"
              disabled={create.isPending || update.isPending || renameStale || reloadingRename}
              onClick={() => void save()}
            >
              {create.isPending || update.isPending ? 'Saving series…' : 'Save series'}
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

function SeriesEditions({ seriesId }: { seriesId: string }) {
  const query = useSeriesEditions(seriesId)
  const editions = query.isError ? [] : (query.data?.pages.flatMap(page => page.editions) ?? [])
  return (
    <section aria-label="Series editions" className="space-y-2">
      <h3 className="font-medium">Available editions</h3>
      {query.isLoading && <p role="status">Loading editions…</p>}
      {query.isError && (
        <p role="alert">
          Edition data unavailable.{' '}
          <Button type="button" variant="link" onClick={() => void query.refetch()}>
            Retry editions
          </Button>
        </p>
      )}
      {query.isSuccess && !editions.length && <p>No available edition data.</p>}
      <ul>
        {editions.map(edition => (
          <li key={edition.showId}>
            <Link className="underline" to={`/${edition.slug}/setup`}>
              {edition.year} · {edition.name}
            </Link>
            {!edition.canEdit && ' · Read only'}
          </li>
        ))}
      </ul>
      {query.hasNextPage && (
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          More editions
        </Button>
      )}
    </section>
  )
}
